// src/components/ClientDetails/DadosGerais.tsx
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { leadSchema, LeadForm, ExtendedLead } from './types';
import { useState, useEffect, useMemo } from 'react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useVendedoresSimples } from '@/hooks/useVendedoresSimples';
import { useParceiros } from '@/hooks/useParceiros';
import { usePermissions } from '@/hooks/usePermissions';
import { X } from "lucide-react";
import {
  LEAD_PRODUTO_VALORES,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS,
  PRODUTO_LEAD_CARREGADOR_LEGADO,
  PRODUTO_LEAD_PARCERIA_CRM,
  isProdutoLeadCarregador,
} from '@/constants/leadsProduto';
import { formatCNPJ } from '@/utils/cpfValidation';
import ProdutosInteressados from './ProdutosInteressados';
import { formatNomeProprio } from '@/utils/formatNomeProprio';

const origemOptions = [
  "Leads de Representantes",
  "Cold Email Marketing",
  "Cold Calls (Call Center)",
  "Pesquisas",
  "Google Ads",
  "Facebook Ads",
  "Campanhas de Tráfego Pago",
  "Campanhas de Indicação",
  "Captação Site (SEO)",
  "Canal YouTube",
  "Plataforma IA-BestSolar",
  "Calculadoras",
  "E-Books",
  "LinkedIn",
  "Instagram",
  "Google Meu Negócio",
  "Parceiros",
  "Landing Page",
  "Outro",
];
const tipoFixacaoOptions = ["Telhado", "Solo", "Carport"];
const tipoTelhaOptions = ["Telha Metálica", "Telha Cerâmica", "Telha de Fibrocimento", "Laje", "Telha zipada"];

/** Catálogo em Clientes / detalhe: sem prospecção parceiro (funil CRM Parceiros). */
const produtoOptionsCatalogoClientes = LEAD_PRODUTO_VALORES.filter(
  (p) => p !== PRODUTO_LEAD_PARCERIA_CRM
);

// Funções de formatação
const formatCEP = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 8);
  return value.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

const formatCPF = (cpf: string | undefined) => {
  if (!cpf) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = cpf.replace(/\D/g, '');
  // Formata no padrão brasileiro 000.000.000-00
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
  }
  // Retorna o número original se não conseguir formatar
  return cpf;
};

const formatTelefoneEdit = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const formatCPFEdit = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

const aplicarFormatoNomeNoBlur = (
  valor: string,
  onChange: (value: string) => void
) => {
  const formatado = formatNomeProprio(valor);
  if (formatado !== valor) {
    onChange(formatado);
  }
};

const DadosGerais = ({ 
  leadData, 
  onUpdate, 
  onUnsavedChanges, 
  onDelete,
  defaultIsEditing = false,
  readOnly = false
}: { 
  leadData: ExtendedLead; 
  onUpdate: () => void; 
  onUnsavedChanges: (has: boolean) => void; 
  onDelete: () => void;
  defaultIsEditing?: boolean;
  readOnly?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(readOnly ? false : defaultIsEditing);
  const permissions = usePermissions();
  const isPartner = permissions.isPartner();
  const isVendedor = permissions.userRole === 'vendedor' || permissions.userProfile?.role === 'vendedor';
  const isAdmin = permissions.userRole === 'admin' || permissions.userProfile?.role === 'admin';
  const representativeId = permissions.userProfile?.id;
  
  const { vendedores, loading: loadingVendedores } = useVendedoresSimples();

  const form = useForm<LeadForm>({
    resolver: zodResolver(leadSchema),
    defaultValues: leadData || {},
  });

  const { control, handleSubmit, watch, setValue, reset, formState: { isDirty } } = form;
  
  const vendedorSelecionado = watch('vendedor_id') || '';
  const [vendedorAnterior, setVendedorAnterior] = useState(vendedorSelecionado);
  
  // Hook para carregar parceiros baseado no vendedor selecionado
  // Prioriza o vendedor_id do leadData se disponível, senão usa o selecionado no form
  const vendedorIdParaParceiros = isVendedor 
    ? representativeId 
    : (isAdmin ? (vendedorSelecionado || leadData?.vendedor_id) : undefined);
  const { parceiros, loading: loadingParceiros } = useParceiros(vendedorIdParaParceiros);
  
  if (import.meta.env.DEV) {
    console.log('🔍 DadosGerais - Parceiros:', {
      vendedorIdParaParceiros,
      parceirosLength: parceiros.length,
      loadingParceiros,
      isVendedor,
      isAdmin,
      representativeId,
      vendedorSelecionado,
      leadDataVendedorId: leadData?.vendedor_id,
      leadDataParceiroId: leadData?.parceiro_id
    });
  }

  // Atualizar form quando leadData mudar
  useEffect(() => {
    if (leadData) {
      // Converter valores null para undefined/string vazia para evitar problemas de validação
      // O Zod agora permite null, mas para os campos do formulário (especialmente Select e Input),
      // é melhor usar string vazia ou undefined para evitar avisos de componentes controlados
      const processedData = {
        ...leadData,
        nome: leadData.nome || '',
        cpf: leadData.cpf || '',
        razaoSocial: leadData.razaoSocial || '',
        cnpj: leadData.cnpj || '',
        representanteLegal: leadData.representanteLegal || '',
        cpfRepresentante: leadData.cpfRepresentante || '',
        email: leadData.email || '',
        cep: leadData.cep || '',
        endereco: leadData.endereco || '',
        numero: leadData.numero || '',
        cidade: leadData.cidade || '',
        estado: leadData.estado || '',
        mensagem: leadData.mensagem || '',
        consumoMensal: leadData.consumoMensal ? Number(leadData.consumoMensal) : undefined,
        tipoFixacao: leadData.tipoFixacao || undefined,
        tipoTelha: leadData.tipoTelha || '',
        vendedor_id: leadData.vendedor_id || '',
        parceiro_id: leadData.parceiro_id || '',
        seguirProcessoTarefas: leadData.seguirProcessoTarefas !== false,
      };
      
      reset(processedData);
      if (import.meta.env.DEV) {
        console.log('🔄 DadosGerais - Form resetado com leadData:', {
          parceiro_id: processedData.parceiro_id,
          vendedor_id: processedData.vendedor_id,
          parceiroNome: leadData.parceiroNome,
          consumoMensal: processedData.consumoMensal,
          tipoFixacao: processedData.tipoFixacao
        });
      }
    }
  }, [leadData, reset]);

  // Garantir que parceiro_id seja setado quando os parceiros forem carregados
  // IMPORTANTE: Só roda quando não está editando para não interferir com mudanças do usuário
  useEffect(() => {
    if (!loadingParceiros && !isEditing && leadData?.parceiro_id && parceiros.length > 0) {
      const parceiroExiste = parceiros.find(p => p.id === leadData.parceiro_id);
      if (parceiroExiste) {
        setValue('parceiro_id', leadData.parceiro_id, { shouldDirty: false });
        if (import.meta.env.DEV) {
          console.log('✅ Parceiro setado no form:', parceiroExiste.nome);
        }
      } else if (import.meta.env.DEV) {
        console.warn('⚠️ Parceiro não encontrado na lista:', leadData.parceiro_id);
      }
    }
  }, [loadingParceiros, parceiros, leadData?.parceiro_id, setValue, isEditing]);

  useEffect(() => {
    onUnsavedChanges(isDirty && isEditing);
  }, [isDirty, isEditing, onUnsavedChanges]);
  
  // Resetar parceiro_id APENAS quando vendedor mudar de fato (não na inicialização)
  useEffect(() => {
    if (isAdmin && vendedorSelecionado && isEditing && vendedorAnterior && vendedorSelecionado !== vendedorAnterior) {
      setValue('parceiro_id', '');
      if (import.meta.env.DEV) {
        console.log('🔄 Parceiro resetado devido à mudança de vendedor');
      }
    }
    setVendedorAnterior(vendedorSelecionado);
  }, [isAdmin, vendedorSelecionado, isEditing]);

  const onSubmit = async (data: LeadForm) => {
    try {
      // Preparar dados para atualização
      const updateData: any = { ...data };

      if (updateData.nome) updateData.nome = formatNomeProprio(updateData.nome);
      if (updateData.razaoSocial) updateData.razaoSocial = formatNomeProprio(updateData.razaoSocial);
      if (updateData.representanteLegal) {
        updateData.representanteLegal = formatNomeProprio(updateData.representanteLegal);
      }
      
      // Converter valores undefined para null para campos opcionais
      if (updateData.consumoMensal === undefined) {
        updateData.consumoMensal = null;
      }
      if (updateData.tipoFixacao === undefined) {
        updateData.tipoFixacao = null;
      }
      
      // Se parceiro_id ou vendedor_id forem string vazia, enviar null explicitamente para remover do banco
      if (!updateData.parceiro_id || updateData.parceiro_id === '') {
        updateData.parceiro_id = null;
      }
      if (!updateData.vendedor_id || updateData.vendedor_id === '') {
        updateData.vendedor_id = null;
      }
      
      if (import.meta.env.DEV) {
        console.log('📤 Enviando dados para atualização:', updateData);
      }
      
      const response = await fetchWithAuth(`/api/leads/${leadData.id}/basic`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });
      if (!response.ok) throw new Error('Erro ao atualizar');
      onUpdate();
      alert('Dados atualizados com sucesso');
      setIsEditing(false);
      reset(data); // Atualiza o form com os novos dados salvos
    } catch (error) {
      console.error(error);
      alert('Erro ao atualizar dados');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset(leadData); // Reseta para os valores originais
  };

  const handleDeleteLead = () => {
    onDelete();
  };

  const tipoCliente = watch('tipo') || 'fisica';
  const origemSelecionada = watch('origem') || '';
  const tipoFixacao = watch('tipoFixacao') || '';
  const produtoSelecionado = watch('produto');
  /** Valor legado em `leads.produto` — não entra no catálogo de novos cadastros, mas precisa aparecer no select ao editar. */
  const produtoSelectOptions = useMemo(() => {
    const base = [...produtoOptionsCatalogoClientes];
    const p = produtoSelecionado;
    if (p === PRODUTO_LEAD_ADEQUACOES_REFORMAS && !base.includes(p)) {
      base.push(p);
    }
    if (p === PRODUTO_LEAD_PARCERIA_CRM && !base.includes(p)) {
      base.push(p);
    }
    if (p === PRODUTO_LEAD_CARREGADOR_LEGADO && !base.includes(p)) {
      base.push(p);
    }
    return base;
  }, [produtoSelecionado]);
  const isCarregadores = isProdutoLeadCarregador(
    isEditing ? produtoSelecionado : leadData.produto
  );

  const fetchAddressFromCEP = async (cepValue: string) => {
    if (cepValue.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepValue}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setValue('endereco', data.logradouro);
          setValue('cidade', data.localidade);
          setValue('estado', data.uf);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  if (!leadData) {
    return <div>Carregando dados do cliente...</div>;
  }

  return (
    !isEditing ? (
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Tipo de Cliente</label>
          <p className="text-sm break-words">{leadData.tipo === 'fisica' ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
        </div>
        {leadData.tipo === 'fisica' && (
          <>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">Nome</label>
              <p className="text-sm break-words">{leadData.nome || ''}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">CPF</label>
              <p className="text-sm break-words">{formatCPF(leadData.cpf)}</p>
            </div>
          </>
        )}
        {leadData.tipo === 'juridica' && (
          <>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">Razão Social</label>
              <p className="text-sm break-words">{leadData.razaoSocial || ''}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">CNPJ</label>
              <p className="text-sm break-words">{leadData.cnpj ? formatCNPJ(leadData.cnpj) : ''}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">Representante Legal</label>
              <p className="text-sm break-words">{leadData.representanteLegal || ''}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">CPF Representante</label>
              <p className="text-sm break-words">{formatCPF(leadData.cpfRepresentante)}</p>
            </div>
          </>
        )}
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Email</label>
          <p className="text-sm break-words">{leadData.email || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Telefone</label>
          <p className="text-sm break-words">{formatPhone(leadData.telefone)}</p>
        </div>
        {!isCarregadores && (
          <>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">Consumo Mensal Aproximado (kWh)</label>
              <p className="text-sm break-words">{leadData.consumoMensal || ''}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground block">Tipo de Fixação</label>
              <p className="text-sm break-words">{leadData.tipoFixacao || ''}</p>
            </div>
            {leadData.tipoFixacao === 'Telhado' && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-muted-foreground block">Tipo de Telha</label>
                <p className="text-sm break-words">{leadData.tipoTelha || ''}</p>
              </div>
            )}
          </>
        )}
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">CEP</label>
          <p className="text-sm break-words">{leadData.cep ? formatCEP(leadData.cep) : ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Endereço</label>
          <p className="text-sm break-words">{leadData.endereco || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Número</label>
          <p className="text-sm break-words">{leadData.numero || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Cidade</label>
          <p className="text-sm break-words">{leadData.cidade || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Estado</label>
          <p className="text-sm break-words">{leadData.estado || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Origem</label>
          <p className="text-sm break-words">{leadData.origem === 'Outro' && leadData.origemOutro ? leadData.origemOutro : leadData.origem || ''}</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Produto</label>
          <p className="text-sm break-words">{leadData.produto || ''}</p>
        </div>
        {leadData.id && (
          <ProdutosInteressados
            leadId={leadData.id}
            produtoAtual={leadData.produto}
            canEdit={!readOnly}
            onChanged={onUpdate}
          />
        )}
        {!isVendedor && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground block">Vendedor</label>
            <p className="text-sm break-words">{leadData.vendedorNome || ''}</p>
          </div>
        )}
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Parceiro</label>
          <p className="text-sm break-words">
            {leadData.parceiroNome 
              ? `${leadData.parceiroNome}${leadData.parceiroCidade ? ` - ${leadData.parceiroCidade}/${leadData.parceiroEstado}` : ''}`
              : 'Nenhum parceiro vinculado'
            }
          </p>
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-sm font-medium text-muted-foreground block">Mensagem</label>
          <p className="text-sm break-words whitespace-pre-wrap">{leadData.mensagem || ''}</p>
        </div>
        {!readOnly && (
          <div className="col-span-2 flex gap-2">
            <Button onClick={() => setIsEditing(true)}>Editar</Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (confirm('Tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita e removerá todos os dados relacionados.')) {
                  handleDeleteLead();
                }
              }}
            >
              Excluir Lead
            </Button>
          </div>
        )}
      </div>
    ) : (
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={control}
            name="tipo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Cliente</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="fisica">Pessoa Física</SelectItem>
                    <SelectItem value="juridica">Pessoa Jurídica</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {tipoCliente === 'fisica' && (
            <>
              <FormField
                control={control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        onBlur={(e) => {
                          field.onBlur();
                          aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatCPFEdit(field.value || '')}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        placeholder="000.000.000-00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          {tipoCliente === 'juridica' && (
            <>
              <FormField
                control={control}
                name="razaoSocial"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Razão Social *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        onBlur={(e) => {
                          field.onBlur();
                          aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CNPJ</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatCNPJ(field.value || '')}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        placeholder="00.000.000/0000-00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="representanteLegal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Representante Legal *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        onBlur={(e) => {
                          field.onBlur();
                          aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="cpfRepresentante"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF Representante</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatCPFEdit(field.value || '')}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        placeholder="000.000.000-00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formatTelefoneEdit(field.value || '')}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                    placeholder="(00) 00000-0000"
                  />
                </FormControl>
                <FormMessage />
            </FormItem>
          )}
        />
        {!isCarregadores && (
          <>
            <FormField
              control={control}
              name="consumoMensal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Consumo Mensal Aproximado (kWh)</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      type="number" 
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === '' ? undefined : Number(value));
                      }} 
                      value={field.value || ''} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="tipoFixacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Fixação</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(value === '' ? undefined : value)} 
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de fixação" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tipoFixacaoOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {tipoFixacao === 'Telhado' && (
              <FormField
                control={control}
                name="tipoTelha"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Telha *</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === '' ? '' : value)} 
                      value={field.value || ''}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo de telha" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {tipoTelhaOptions.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </>
        )}
        <FormField
          control={control}
          name="cep"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CEP</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    value={formatCEP(field.value || '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      field.onChange(value);
                      fetchAddressFromCEP(value);
                    }}
                    placeholder="00000-000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="endereco"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="numero"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Número</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="cidade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="estado"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="origem"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Origem *</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  value={field.value || ''}
                  disabled={isPartner}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {origemOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {origemSelecionada === 'Outro' && (
            <FormField
              control={control}
              name="origemOutro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Especificar Origem</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      value={field.value || ''} 
                      disabled={isPartner}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="produto"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Produto desta negociação *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ''}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o produto" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {produtoSelectOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {leadData.id && (
            <ProdutosInteressados
              leadId={leadData.id}
              produtoAtual={produtoSelecionado || leadData.produto}
              canEdit
              onChanged={onUpdate}
            />
          )}
          <FormField
            control={control}
            name="mensagem"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensagem</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Adicione informações ou observações sobre o lead"
                    className="min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {!isVendedor && !isPartner && (
            <FormField
              control={control}
              name="vendedor_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor *</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    value={field.value || ''}
                    disabled={loadingVendedores}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={loadingVendedores ? "Carregando..." : "Selecione o vendedor"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vendedores.map((vendedor) => (
                        <SelectItem key={vendedor.id} value={vendedor.id}>{vendedor.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {/* Campo Parceiro - Não aparece para parceiros, é opcional para vendedores e admin */}
          {!isPartner && (
            <FormField
              control={control}
              name="parceiro_id"
              render={({ field }) => {
                if (import.meta.env.DEV) {
                  console.log('🔍 Campo Parceiro render:', {
                    fieldValue: field.value,
                    leadDataParceiroId: leadData?.parceiro_id,
                    parceirosLength: parceiros.length,
                    loadingParceiros,
                    vendedorSelecionado,
                    vendedorIdParaParceiros
                  });
                }
                
                return (
                  <FormItem>
                    <FormLabel>Parceiro (Opcional)</FormLabel>
                    <div className="flex gap-2">
                      <Select 
                        onValueChange={(value) => {
                          field.onChange(value);
                          setValue('parceiro_id', value);
                        }}
                        value={field.value && field.value !== '' ? field.value : undefined}
                        disabled={loadingParceiros || (isAdmin && !vendedorSelecionado)}
                      >
                        <FormControl>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder={
                              loadingParceiros ? "Carregando..." : 
                              (isAdmin && !vendedorSelecionado) ? "Selecione o vendedor primeiro" :
                              parceiros.length === 0 ? "Nenhum parceiro disponível" :
                              "Selecione o parceiro (opcional)"
                            } />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {parceiros.map((parceiro) => (
                            <SelectItem key={parceiro.id} value={parceiro.id}>
                              {parceiro.nome} {parceiro.cidade && `- ${parceiro.cidade}/${parceiro.estado}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.value && field.value !== '' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            field.onChange('');
                            setValue('parceiro_id', '', { shouldDirty: true, shouldTouch: true });
                            if (import.meta.env.DEV) {
                              console.log('🗑️ Parceiro limpo pelo usuário');
                            }
                          }}
                          title="Limpar seleção"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Deixe em branco se não houver parceiro vinculado
                    </p>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}
          <div className="flex gap-2">
            <Button type="submit">Salvar Alterações</Button>
            <Button type="button" variant="outline" onClick={handleCancel}>Cancelar</Button>
          </div>
        </form>
      </Form>
    )
  );
};

export default DadosGerais;
